import React, { useState, useEffect, useCallback } from 'react';
import {
  getFirestore,
  doc,
  getDoc,
  collection,
  query,
  where,
  orderBy,
  getDocs,
  collectionGroup,
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { Box, Grid, Container, Typography } from '@mui/material';
import { Routes, Route, useLocation } from 'react-router-dom';
import { functions, db } from "../firebase"; 
import Page from '../components/Page';

import { useUser } from '../UserContext';

import MemberSubscriptionCard from './MemberSubscriptionCard';
import MemberPennantsCard from './MemberPennantsCard';
import MemberMatchHistoryCard from './MemberMatchHistoryCard';
import MemberUpcomingMatchesCard from './MemberUpcomingMatchesCard';
import MemberRankingsCard from './MemberRankingsCard';
import MemberFillInsCard from './MemberFillInsCard';

import Profile from './Profile';
import Plans from '../plans/Plans';
import Calendar from '../calendars/Calendar';
import SinglePennantLadder from './SinglePennantLadder';

import MemberRegisterFillInDialog from './MemberRegisterFillInDialog';

const parseDate = (dateString) => {
  const [day, month, year] = dateString.split('/').map(Number);
  return new Date(year, month - 1, day);
};

export default function MemberDashboardApp() {
  const { user, memberData } = useUser();
  const { pathname } = useLocation();

  const [loading, setLoading] = useState(true);
  const [localMemberData, setLocalMemberData] = useState(null);
  const [subscriptions, setSubscriptions] = useState([]);
  const [pennants, setPennants] = useState([]);
  const [recentMatches, setRecentMatches] = useState([]);
  const [upcomingMatches, setUpcomingMatches] = useState([]);
  const [fillIns, setFillIns] = useState([]);

  const [fillInDialogOpen, setFillInDialogOpen] = useState(false);

  const fetchMemberExtras = useCallback(async () => {
    if (!user) {
      console.error('No user logged in.');
      return;
    }

    const docId = user.squareCustomerId || user.uid;
    if (!docId) {
      console.error('No valid doc ID.');
      return;
    }

    setLoading(true);
    try {
      if (user.squareCustomerId) {
        const fetchSquareCustomer = httpsCallable(functions, 'fetchSquareCustomer');
        await fetchSquareCustomer({ customerId: user.squareCustomerId });
      }

      const memberRef = doc(db, 'members', docId);
      const memberSnap = await getDoc(memberRef);

      if (!memberSnap.exists()) {
        console.error(`No Firestore document found for member with ID: ${docId}`);
        setLocalMemberData(null);
        setSubscriptions([]);
        setPennants([]);
        setRecentMatches([]);
        setUpcomingMatches([]);
        setFillIns([]);
        return;
      }

      const freshMemberData = memberSnap.data();
      setLocalMemberData(freshMemberData);
      setSubscriptions(freshMemberData.subscriptions || []);
      setPennants(freshMemberData.pennants || []);

      const matchesRef = collection(db, 'Matches');
      const team1Query = query(
        matchesRef,
        where('team1.id', '==', docId),
        orderBy('matchDate', 'desc')
      );
      const team2Query = query(
        matchesRef,
        where('team2.id', '==', docId),
        orderBy('matchDate', 'desc')
      );
      const [team1Snap, team2Snap] = await Promise.all([
        getDocs(team1Query),
        getDocs(team2Query),
      ]);
      const team1Matches = team1Snap.docs.map((m) => ({ id: m.id, ...m.data() }));
      const team2Matches = team2Snap.docs.map((m) => ({ id: m.id, ...m.data() }));
      const allMatches = [...team1Matches, ...team2Matches].sort(
        (a, b) => b.matchDate.toMillis() - a.matchDate.toMillis()
      );
      setRecentMatches(allMatches);

      const allTeams = await Promise.all(
        (freshMemberData.pennants || []).map(async (pennant) => {
          const teamsRef = collection(db, 'Pennants', pennant.pennantId, 'teams');
          const teamsSnap = await getDocs(teamsRef);
          return teamsSnap.docs.map((doc) => ({
            pennantId: pennant.pennantId,
            pennantName: pennant.name,
            teamId: doc.id,
            ...doc.data(),
          }));
        })
      );
      const flatTeams = allTeams.flat();
      const userTeams = flatTeams.filter((team) =>
        Object.values(team.members).some((member) => member.id === docId)
      );

      const allDraws = await Promise.all(
        (freshMemberData.pennants || []).map(async (pennant) => {
          const pennantDocSnap = await getDoc(doc(db, 'Pennants', pennant.pennantId));
          const pennantData = pennantDocSnap.exists() ? pennantDocSnap.data() : {};
          const drawsRef = collection(db, 'Pennants', pennant.pennantId, 'draws');
          const drawsSnap = await getDocs(drawsRef);
          return drawsSnap.docs.map((doc) => ({
            pennantId: pennant.pennantId,
            pennantName: pennant.name,
            sportType: pennantData.sport,
            ...doc.data(),
          }));
        })
      );
      const flatDraws = allDraws.flat();
      const now = new Date();
      now.setHours(0, 0, 0, 0);
      const upcoming = flatDraws.flatMap((draw) =>
        draw.weeks.flatMap((week) =>
          week.matches
            .filter(
              (match) =>
                parseDate(week.date) >= now &&
                (
                  match.team1?.id === docId ||
                  match.team2?.id === docId ||
                  userTeams.some(
                    (team) =>
                      team.teamId === match.team1?.id ||
                      team.teamId === match.team2?.id
                  )
                )
            )
            .map((match) => ({
              ...match,
              matchDate: week.date,
              pennantName: draw.pennantName,
              sportType: draw.sportType,
              section: week.section,
            }))
        )
      );
      const sortedUpcoming = upcoming.sort(
        (a, b) => parseDate(a.matchDate) - parseDate(b.matchDate)
      );
      setUpcomingMatches(sortedUpcoming);

      const fillInsQuery = query(
        collectionGroup(db, 'availableFillIns'),
        where('memberId', '==', docId)
      );
      const fillInsSnap = await getDocs(fillInsQuery);
      const fillInsData = fillInsSnap.docs.map((docSnap) => {
        const pennantId = docSnap.ref.parent.parent.id;
        return {
          docId: docSnap.id,
          pennantId,
          ...docSnap.data(),
        };
      });
      setFillIns(fillInsData);

    } catch (error) {
      console.error('Error fetching member data:', error);
      setLocalMemberData(null);
      setSubscriptions([]);
      setPennants([]);
      setRecentMatches([]);
      setUpcomingMatches([]);
      setFillIns([]);
    } finally {
      setLoading(false);
    }
  }, [user, functions]);

  useEffect(() => {
    if (memberData) {
      setLocalMemberData(memberData);
      setSubscriptions(memberData.subscriptions || []);
      setPennants(memberData.pennants || []);
    }
  }, [memberData]);

  useEffect(() => {
    fetchMemberExtras();
  }, [fetchMemberExtras]);

  const handlePennantJoined = useCallback(async () => {
    await fetchMemberExtras();
  }, [fetchMemberExtras]);

  if (!user) {
    return <Typography variant="h6">Please log in...</Typography>;
  }

  if (loading) {
    return <Typography>Loading Member Dashboard...</Typography>;
  }

  if (!localMemberData) {
    return (
      <Typography variant="h6" color="error">
        No member data available. Please contact support.
      </Typography>
    );
  }

  return (
    <Page title="Member Dashboard | Ballarat Squash and Racquetball">
      <Container maxWidth="xl">
        <Box sx={{ pb: 5 }}>
          <Typography variant="h4">
            Hello, {localMemberData.firstName || user.email}!
          </Typography>
          <Typography variant="subtitle1" sx={{ color: 'text.secondary' }}>
            Welcome to your Member Dashboard
          </Typography>
        </Box>

        <Routes>
          <Route
            index
            element={
              <Grid container spacing={3}>
                <Grid item xs={12} sm={6} md={4}>
                  <MemberSubscriptionCard subscriptions={subscriptions} />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <MemberPennantsCard
                    pennants={pennants}
                    onPennantJoined={handlePennantJoined}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <MemberFillInsCard
                    fillIns={fillIns}
                    onFillInRegistered={handlePennantJoined}
                  />
                </Grid>
                
                <Grid item xs={12} sm={6} md={4}>
                  <MemberMatchHistoryCard matches={recentMatches} />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <MemberUpcomingMatchesCard matches={upcomingMatches} />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <MemberRankingsCard rankings={localMemberData.rankings || {}} />
                </Grid>
                
              </Grid>
            }
          />
          <Route path="profile" element={<Profile />} />
          <Route path="plans" element={<Plans />} />
          <Route path="events" element={<Calendar />} />
          <Route path="pennant/:pennantId/ladder" element={<SinglePennantLadder />} />
        </Routes>

        <MemberRegisterFillInDialog
          open={fillInDialogOpen}
          onClose={() => setFillInDialogOpen(false)}
          onFillInRegistered={handlePennantJoined}
        />
      </Container>
    </Page>
  );
}
